// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { t } from '@/i18n';
import type { BffWebSocketClient } from '@/services/bffWebSocket';
import {
  acknowledgeRejection,
  getOfflineQueueState,
  subscribeOfflineQueue,
  type OfflineQueueState,
} from '@/services/offlineQueue';

type ConnectionStatusPillProps = {
  client: BffWebSocketClient | null;
  subjectKey: string;
};

/**
 * Small status pill for the hole screen: shows when the device is offline with
 * locally saved entries, while those entries sync after reconnect, and when a
 * queued entry was rejected (e.g. the round finished while offline).
 */
export const ConnectionStatusPill = ({ client, subjectKey }: ConnectionStatusPillProps) => {
  const [connected, setConnected] = useState(() => client?.isConnected() ?? false);
  const [queueState, setQueueState] = useState<OfflineQueueState>(() => getOfflineQueueState(subjectKey));
  const [showSynced, setShowSynced] = useState(false);
  const previousPendingRef = useRef(queueState.pendingCount);
  const syncedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!client) return;
    setConnected(client.isConnected());
    const unsubscribe = client.onConnectionChange((status) => {
      setConnected(status === 'connected');
    });
    return () => {
      unsubscribe();
    };
  }, [client]);

  useEffect(() => {
    setQueueState(getOfflineQueueState(subjectKey));
    return subscribeOfflineQueue((state, forSubject) => {
      if (forSubject !== subjectKey) return;
      setQueueState(state);
    });
  }, [subjectKey]);

  useEffect(() => {
    const previous = previousPendingRef.current;
    previousPendingRef.current = queueState.pendingCount;
    if (previous > 0 && queueState.pendingCount === 0 && !queueState.syncing) {
      setShowSynced(true);
      if (syncedTimerRef.current) clearTimeout(syncedTimerRef.current);
      syncedTimerRef.current = setTimeout(() => setShowSynced(false), 3000);
    }
    return () => {
      if (syncedTimerRef.current) {
        clearTimeout(syncedTimerRef.current);
        syncedTimerRef.current = null;
      }
    };
  }, [queueState.pendingCount, queueState.syncing]);

  const hasWarning = queueState.lastRejection !== null || queueState.deadLetteredCount > 0;

  if (hasWarning) {
    const message =
      queueState.lastRejection?.code === 'round.closedForScoring'
        ? t('hole.offlineRoundClosed')
        : t('hole.offlineSyncFailed', { count: Math.max(queueState.deadLetteredCount, 1) });
    return (
      <Pressable onPress={() => acknowledgeRejection(subjectKey)} style={[pillStyles.pill, pillStyles.warning]}>
        <Text style={pillStyles.warningText}>{message}</Text>
      </Pressable>
    );
  }

  if (queueState.pendingCount > 0) {
    return (
      <View style={[pillStyles.pill, queueState.syncing && connected ? pillStyles.syncing : pillStyles.offline]}>
        <Text style={pillStyles.text}>
          {queueState.syncing && connected
            ? t('hole.offlineSyncing')
            : t('hole.offlinePending', { count: queueState.pendingCount })}
        </Text>
      </View>
    );
  }

  if (showSynced) {
    return (
      <View style={[pillStyles.pill, pillStyles.synced]}>
        <Text style={pillStyles.text}>{t('hole.offlineSynced')}</Text>
      </View>
    );
  }

  return null;
};

const pillStyles = StyleSheet.create({
  pill: {
    alignSelf: 'center',
    borderRadius: 14,
    marginBottom: 6,
    marginTop: 2,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  offline: {
    backgroundColor: '#B7791F',
  },
  syncing: {
    backgroundColor: '#2B6CB0',
  },
  synced: {
    backgroundColor: '#2F855A',
  },
  warning: {
    backgroundColor: '#C53030',
  },
  text: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  warningText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
});
